import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BaseClass } from '../../api/BaseClass';

const apiRoot = process.env.REACT_APP_API_URL || '';

function formatBytes(bytes) {
  if (!bytes) return '0 Bytes';

  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return (bytes / Math.pow(1024, unitIndex)).toFixed(unitIndex === 0 ? 0 : 1) + ' ' + units[unitIndex];
}

async function getAuthorizationHeaders() {
  const token = await new BaseClass().getAccessToken();
  return { Authorization: 'Bearer ' + token };
}

export default function MusicOnHoldFiles() {
  const companyId = useSelector((state) => state.globalInfo.currentCompanyId);
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingFile, setDeletingFile] = useState('');
  const [message, setMessage] = useState(null);

  const loadFiles = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setMessage(null);

    try {
      const headers = await getAuthorizationHeaders();
      const response = await axios.get(apiRoot + '/api/MusicOnHoldFiles', {
        headers,
        params: { companyId }
      });
      setFiles(response.data);
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data || 'Unable to load Music on Hold files.' });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    setSelectedFiles([]);
    loadFiles();
  }, [loadFiles]);

  function chooseFiles(event) {
    const chosenFiles = Array.from(event.target.files || []);
    const invalidFile = chosenFiles.find((file) => !file.name.toLowerCase().endsWith('.wav'));

    if (invalidFile) {
      setSelectedFiles([]);
      setMessage({ severity: 'error', text: 'Only WAV files are supported.' });
      event.target.value = '';
      return;
    }

    setSelectedFiles(chosenFiles);
    setMessage(null);
  }

  async function uploadFiles() {
    if (!selectedFiles.length) return;

    setUploading(true);
    setMessage(null);

    try {
      const headers = await getAuthorizationHeaders();

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file, file.name);

        await axios.post(apiRoot + '/api/MusicOnHoldFiles', formData, {
          headers,
          params: { companyId }
        });
      }

      setSelectedFiles([]);
      if (inputRef.current) inputRef.current.value = '';
      await loadFiles();
      setMessage({ severity: 'success', text: 'Music on Hold file upload complete.' });
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data || 'Unable to upload the selected file(s).' });
    } finally {
      setUploading(false);
    }
  }

  async function deleteFile(fileName) {
    if (!window.confirm('Delete "' + fileName + '"? This cannot be undone.')) return;

    setDeletingFile(fileName);
    setMessage(null);

    try {
      const headers = await getAuthorizationHeaders();
      await axios.delete(apiRoot + '/api/MusicOnHoldFiles', {
        headers,
        params: { companyId, fileName }
      });
      await loadFiles();
      setMessage({ severity: 'success', text: fileName + ' was deleted.' });
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data || 'Unable to delete the file.' });
    } finally {
      setDeletingFile('');
    }
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>Music on Hold</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Upload WAV files for this company. Uploading a file with the same name replaces the existing file.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Button variant="outlined" component="label" disabled={uploading}>
            Choose WAV files
            <input
              ref={inputRef}
              type="file"
              accept=".wav,audio/wav"
              multiple
              hidden
              onChange={chooseFiles}
            />
          </Button>
          <Typography sx={{ flexGrow: 1 }} color={selectedFiles.length ? 'text.primary' : 'text.secondary'}>
            {selectedFiles.length
              ? selectedFiles.map((file) => file.name).join(', ')
              : 'No files selected'}
          </Typography>
          <Button
            variant="contained"
            disabled={!selectedFiles.length || uploading}
            onClick={uploadFiles}
            startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {uploading ? 'Uploading' : 'Upload'}
          </Button>
        </Stack>
      </Paper>

      {message && <Alert severity={message.severity} sx={{ mb: 3 }}>{message.text}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File name</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Last updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} align="center"><CircularProgress size={28} /></TableCell>
              </TableRow>
            )}
            {!loading && files.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">No Music on Hold files have been uploaded.</TableCell>
              </TableRow>
            )}
            {!loading && files.map((file) => (
              <TableRow key={file.fileName}>
                <TableCell>{file.fileName}</TableCell>
                <TableCell>{formatBytes(file.size)}</TableCell>
                <TableCell>{file.updated ? new Date(file.updated).toLocaleString() : '-'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label={'Delete ' + file.fileName}
                    color="error"
                    disabled={deletingFile === file.fileName}
                    onClick={() => deleteFile(file.fileName)}
                  >
                    {deletingFile === file.fileName
                      ? <CircularProgress size={22} />
                      : <DeleteOutlineIcon />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}